import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
  Suspense,
} from "solid-js";
import {
  type NavigateOptions,
  Outlet,
  type RegisteredRouter,
  type RoutePaths,
  useLocation,
  useNavigate,
  useRouterState,
} from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { MessageDescriptor } from "@lingui/core";

import { type ListGroup, type ListWithMeta, mapSmartViewCounts } from "@listto/shared";
import { useSmartViewCountsQuery } from "@listto/shared/solid";
import type { DragEvent } from "@thisbeyond/solid-dnd";

import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { appState } from "@/lib/app-state";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ListThemeProvider } from "@/providers/list-theme-provider";
import { TodoDetailRouteView } from "@/features/todos/components/todo-detail-route-view";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { AppLayoutContext } from "../context";
import { SwipeIndicators } from "./swipe-indicators";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppThemeState } from "../hooks/use-app-theme-state";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { getListNameForPath, getOrderedListPaths, type SidebarQueryResult } from "../helpers";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
  activeListId: string | null;
  activeDragId: () => string | null;
  handleDragStart: (e: DragEvent) => void;
  handleDragEnd: (e: DragEvent) => void;
  lists: SidebarQueryResult<ListWithMeta>;
  listGroups: SidebarQueryResult<ListGroup>;
}

function getAdjacentListNamesHelper(
  paths: string[],
  currentIndex: number,
  lists: ListWithMeta[] | undefined,
  t: (message: string | MessageDescriptor) => string,
) {
  if (paths.length === 0 || currentIndex === -1) {
    return { prev: "", next: "" };
  }
  const nextIndex = (currentIndex + 1) % paths.length;
  const prevIndex = (currentIndex - 1 + paths.length) % paths.length;
  const listData = lists ?? [];

  return {
    prev: getListNameForPath(paths[prevIndex] ?? "", t, listData),
    next: getListNameForPath(paths[nextIndex] ?? "", t, listData),
  };
}

function getNextPath(isNext: boolean, paths: string[], currentIndex: number): string | undefined {
  if (paths.length === 0 || currentIndex === -1) {
    return undefined;
  }
  const nextIndex = isNext
    ? (currentIndex + 1) % paths.length
    : (currentIndex - 1 + paths.length) % paths.length;
  return paths[nextIndex];
}

function getCurrentPathIndex(path: string, orderedListPaths: string[]): number {
  if (path.includes("/todo/")) {
    return -1;
  }
  return orderedListPaths.indexOf(path);
}

function getListDialogProps(list: ListWithMeta | null | undefined) {
  if (!list) {
    return undefined;
  }
  return {
    id: list.id,
    name: list.name,
    color: list.color,
    icon: list.icon,
    theme_id: list.theme_id,
    is_blur: list.is_blur,
  };
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

// Non-critical noise from @thisbeyond/solid-dnd caused by virtualization &
// route-transition unmounts: when a sortable row leaves the viewport or the
// list switches, solid-dnd tries to clean up a draggable / droppable /
// transformer whose registration it can no longer find and logs a
// "nonexistent ..." message (including "Cannot remove from droppable ...,
// nonexistent transformer with id: sortableOffset"). All come from solid-dnd's
// registry and are safe to ignore. Match on "nonexistent" + a dnd entity word
// so every variant is caught without swallowing unrelated logs.
export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  // Track the target path during fast keyboard navigation to avoid lag
  const [navigationTargetPath, setNavigationTargetPath] = createSignal<string | null>(null);

  createEffect(() => {
    const currentPath = location().pathname;
    const target = navigationTargetPath();
    if (currentPath === target || routerState().status !== "pending") {
      setNavigationTargetPath(null);
    }
  });

  const orderedListPaths = createMemo(() =>
    getOrderedListPaths(props.lists.data, props.listGroups.data),
  );

  const currentPathIndex = () => {
    const current = navigationTargetPath() ?? location().pathname;
    return getCurrentPathIndex(current, orderedListPaths());
  };

  const getAdjacentListNames = () =>
    getAdjacentListNamesHelper(orderedListPaths(), currentPathIndex(), props.lists.data, t);

  const navigateList = (isNext: boolean) => {
    const targetPath = getNextPath(isNext, orderedListPaths(), currentPathIndex());
    if (targetPath) {
      setNavigationTargetPath(targetPath);
      void navigate({ to: targetPath });
    }
  };

  const {
    swipeListTx,
    isSwipingList,
    swipeListTy,
    isSwipingListY,
    swipeListAnimating,
    swipeListYAnimating,
    LIST_SWIPE_INDICATOR_TRIGGER_PX,
    LIST_SWIPE_COMMIT_THRESHOLD_PX,
  } = usePageSwipeGestures({
    location,
    navigate,
    orderedListPaths,
    currentPathIndex,
    navigateList,
  });

  const isNavigating = () => routerState().status === "pending";

  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const theme = useTheme();

  const {
    listDialogOpen,
    setListDialogOpen,
    creationGroupId,
    shareDialogOpen,
    setShareDialogOpen,
    selectedList,
    isSidebarOpen,
    setIsSidebarOpen,
    choiceDialogOpen,
    setChoiceDialogOpen,
    groupDialogOpen,
    setGroupDialogOpen,
    editingGroup,
    shortcutsOpen,
    setShortcutsOpen,
    openNewListDialog,
    openNewGroupDialog,
    openChoiceDialog,
    openEditListDialog,
    openShareListDialog,
    openEditGroupDialog,
  } = useAppDialogs(location);

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    t,
    listDialogOpen,
    choiceDialogOpen,
    groupDialogOpen,
    shareDialogOpen,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
    openNewListDialog,
    openNewGroupDialog,
    navigateList,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  const counts = useSmartViewCountsQuery({
    db: () => props.db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const stats = () => {
    if (counts.isLoading) {
      return undefined;
    }
    const { data } = counts;
    const s = mapSmartViewCounts(data ?? undefined);
    return {
      all: s.all,
      myDay: s.myDay,
      important: s.important,
      planned: s.planned,
      inbox: s.inbox,
    };
  };

  const {
    listContextId,
    activeTodoId,
    backToRoute,
    backParams,
    activeBlur,
    activeListTokens,
    activeListBackground,
    glows,
  } = useAppThemeState({
    committedPath: () => location().pathname,
    listsData: () => props.lists.data,
    theme,
  });

  const listDialogProps = createMemo(() => getListDialogProps(selectedList()));

  return (
    <ListThemeProvider listId={listContextId()}>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeListBackground()}
        patternType={activeListTokens()?.patternType ?? theme.tokens()?.patternType}
        patternOpacity={activeListTokens()?.patternOpacity ?? theme.tokens()?.patternOpacity}
        {...glows()}
      >
        <SwipeIndicators
          isSwipingList={isSwipingList}
          swipeListTx={swipeListTx}
          isSwipingListY={isSwipingListY}
          swipeListTy={swipeListTy}
          LIST_SWIPE_INDICATOR_TRIGGER_PX={LIST_SWIPE_INDICATOR_TRIGGER_PX}
          LIST_SWIPE_COMMIT_THRESHOLD_PX={LIST_SWIPE_COMMIT_THRESHOLD_PX}
          location={location}
          getAdjacentListNames={getAdjacentListNames}
          t={t}
        />

        <AppLayoutSidebar
          isOpen={isSidebarOpen}
          setOpen={setIsSidebarOpen}
          stats={stats}
          statsLoading={() => stats() === undefined}
        />

        {/* ── Main content ───────────────────────────────────────── */}
        <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div
            class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]"
            style={{
              transform: `translate3d(${swipeListTx()}px, ${swipeListTy()}px, 0)`,
              transition:
                swipeListAnimating() || swipeListYAnimating()
                  ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
                  : "none",
              "will-change":
                isSwipingList() || isSwipingListY() || swipeListAnimating() || swipeListYAnimating()
                  ? "transform"
                  : "auto",
            }}
          >
            <AppLayoutContext.Provider
              value={{
                openNewListDialog,
                openChoiceDialog,
                openEditListDialog,
                openShareListDialog,
                openEditGroupDialog,
                activeDragId: () => props.activeDragId(),
              }}
            >
              <div class="flex flex-1 flex-col min-h-0 h-full">
                <Outlet />
              </div>
            </AppLayoutContext.Provider>
          </div>
        </main>

        <AppLayoutDialogs
          listDialogOpen={listDialogOpen}
          setListDialogOpen={setListDialogOpen}
          listDialogProps={listDialogProps}
          creationGroupId={creationGroupId}
          choiceDialogOpen={choiceDialogOpen}
          setChoiceDialogOpen={setChoiceDialogOpen}
          openNewListDialog={openNewListDialog}
          openNewGroupDialog={openNewGroupDialog}
          groupDialogOpen={groupDialogOpen}
          setGroupDialogOpen={setGroupDialogOpen}
          editingGroup={editingGroup}
          shareDialogOpen={shareDialogOpen}
          setShareDialogOpen={setShareDialogOpen}
          selectedList={selectedList}
          shortcutsOpen={shortcutsOpen}
          setShortcutsOpen={setShortcutsOpen}
        />

        {/* Persistent Global Details Drawer */}
        <Show when={Boolean(activeTodoId())}>
          <Suspense fallback={null}>
            <TodoDetailRouteView
              todoId={activeTodoId() ?? ""}
              backTo={backToRoute()}
              backParams={
                backParams() as NavigateOptions<
                  RegisteredRouter,
                  string,
                  RoutePaths<RegisteredRouter["routeTree"]>
                >["params"]
              }
            />
          </Suspense>
        </Show>
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </ListThemeProvider>
  );
}
